import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';

export interface IndicatorPool {
  price: number;
  direction: string;
  confidence: number;
  rsi: number;
  adx: number;
  bbPosition: number;
  stochK: number;
  macdValue: number;
  macdSignal: number;
  macdHist: number;
  funding: number;
  oiChange: number;
  lsRatio: number;
  fearGreed: number;
  priceChange24h: number;
  support: number;
  resistance: number;
  regime: string;
  cascadeRisk: number;
  temperature: number;
  confluence: number;
  longCount: number;
  shortCount: number;
  neutralCount: number;
  squeezeActive: boolean;
  hmmState: string;
  hmmConfidence: number;
  cvd: number;
  story: string;
  consensusLine: string;
  counterArgument: string;
  bewijsLine: string;
  faseLine: string;
  moederbreinLine: string;
  whale: string;
  chains: any;
  patternName: string;
  patternBias: string;
  patternTimeframe: string;
  patternConfluence: number;
  histDate: string;
  histMove: number;
  histSimilarity: number;
  histWinRate: number;
  liqNearLong: number;
  liqNearShort: number;
  liqNearLongVol: number;
  liqNearShortVol: number;
  liqCascadeLabel: string;
  liqMagnetic: number;
  mcLongProb: number;
  mcShortProb: number;
  mcP5: number;
  mcP50: number;
  mcP95: number;
  mcVar95: number;
  breakoutProb: number;
  breakoutDir: string;
  breakoutMag: string;
  surgTarget: number;
  surgStop: number;
  surgRR: number;
  magneetLine: string;
  maxPainPrice: number;
  putCallRatio: number;
  contradictionLevel: number;
  metaConfScore: number;
  metaAbstain: boolean;
  regimeInTransition: boolean;
  regimeTransProb: number;
  regimeFrom: string;
  regimeTo: string;
  triggerActiveCount: number;
  triggerTotalCount: number;
  triggerNames: string[];
  topEngineNames: string[];
  topEngineDirections: string[];
  topEngineConfidences: number[];
  scenarioName: string;
  scenarioProb: number;
  scenarioProgress: number;
  scenarioSuccessRate: number;
  altScenarioName: string;
  altScenarioProb: number;
  altScenarioDir: string;
  crossExDivergence: string;
  crossExLeading: string;
  fundingLabel: string;
  exBalChange: number;
  liqZoneCount: number;
  liqZoneNearestDist: number;
  tempLabel: string;
  deathZone: boolean;
  deathZoneReason: string;
  briefingHeadline: string;
  gameChangerTiming: string;
  modelAccuracyLast30d: any;
  mcExpectedReturn: number;
  mcNeutralProb: number;
  weightedAgreement: number;
  targetUp: number;
  targetDown: number;
  cascadeProximity: number;
  cvdContext: string;
  oiContext: string;
  blackSwanProb: number;
  volatilityState: string;
  currentSession: string;
  ahr999: number;
  ahr999Phase: string;
  puellMultiple: number;
  puellZone: string;
  piCycleTop: boolean;
  socialVolume: number;
  sentimentScore: number;
  networkHealth: string;
  whaleActivity: string;
  altDataOverall: string;
  chartConfDir: string;
  chartConfScore: number;
  synergyBoost: number;
  topSynergyEngines: string;
  driftScore: number;
  rlAction: string;
  indicatorFeed: string[];
  magnetETA: string;
}

export interface PriceData {
  price: number;
  priceChange24h: number;
  direction: string;
  confidence: number;
}

export interface MarketData {
  regime: string;
  funding: number;
  oiChange: number;
  lsRatio: number;
  fearGreed: number;
  cascadeRisk: number;
  temperature: number;
  confluence: number;
  squeezeActive: boolean;
  hmmState: string;
  hmmConfidence: number;
  volatilityState: string;
  currentSession: string;
  deathZone: boolean;
  deathZoneReason: string;
  breakoutProb: number;
  breakoutDir: string;
  breakoutMag: string;
  regimeInTransition: boolean;
  regimeTransProb: number;
  regimeFrom: string;
  regimeTo: string;
  contradictionLevel: number;
  cascadeProximity: number;
}

interface PoolContextType {
  pool: IndicatorPool | null;
  loading: boolean;
  error: string | null;
  lastUpdate: number;
}

interface PriceContextType {
  priceData: PriceData | null;
  loading: boolean;
}

interface MarketContextType {
  marketData: MarketData | null;
  loading: boolean;
}

const PoolContext = createContext<PoolContextType>({
  pool: null,
  loading: true,
  error: null,
  lastUpdate: 0,
});

const PriceContext = createContext<PriceContextType>({
  priceData: null,
  loading: true,
});

const MarketContext = createContext<MarketContextType>({
  marketData: null,
  loading: true,
});

function extractPriceData(pool: IndicatorPool): PriceData {
  return {
    price: pool.price,
    priceChange24h: pool.priceChange24h,
    direction: pool.direction,
    confidence: pool.confidence,
  };
}

function extractMarketData(pool: IndicatorPool): MarketData {
  return {
    regime: pool.regime,
    funding: pool.funding,
    oiChange: pool.oiChange,
    lsRatio: pool.lsRatio,
    fearGreed: pool.fearGreed,
    cascadeRisk: pool.cascadeRisk,
    temperature: pool.temperature,
    confluence: pool.confluence,
    squeezeActive: pool.squeezeActive,
    hmmState: pool.hmmState,
    hmmConfidence: pool.hmmConfidence,
    volatilityState: pool.volatilityState,
    currentSession: pool.currentSession,
    deathZone: pool.deathZone,
    deathZoneReason: pool.deathZoneReason,
    breakoutProb: pool.breakoutProb,
    breakoutDir: pool.breakoutDir,
    breakoutMag: pool.breakoutMag,
    regimeInTransition: pool.regimeInTransition,
    regimeTransProb: pool.regimeTransProb,
    regimeFrom: pool.regimeFrom,
    regimeTo: pool.regimeTo,
    contradictionLevel: pool.contradictionLevel,
    cascadeProximity: pool.cascadeProximity,
  };
}

function priceDataChanged(prev: PriceData | null, next: PriceData): boolean {
  if (!prev) return true;
  return prev.price !== next.price || prev.priceChange24h !== next.priceChange24h || prev.direction !== next.direction || prev.confidence !== next.confidence;
}

function marketDataChanged(prev: MarketData | null, next: MarketData): boolean {
  if (!prev) return true;
  return (
    prev.regime !== next.regime ||
    prev.funding !== next.funding ||
    prev.oiChange !== next.oiChange ||
    prev.fearGreed !== next.fearGreed ||
    prev.cascadeRisk !== next.cascadeRisk ||
    prev.hmmState !== next.hmmState ||
    prev.deathZone !== next.deathZone ||
    prev.breakoutProb !== next.breakoutProb ||
    prev.regimeInTransition !== next.regimeInTransition ||
    prev.volatilityState !== next.volatilityState
  );
}

export function PoolProvider({ children }: { children: React.ReactNode }) {
  const [pool, setPool] = useState<IndicatorPool | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState(0);
  const [priceData, setPriceData] = useState<PriceData | null>(null);
  const [marketData, setMarketData] = useState<MarketData | null>(null);
  const mountedRef = useRef(true);
  const prevPriceRef = useRef<PriceData | null>(null);
  const prevMarketRef = useRef<MarketData | null>(null);

  const fetchPool = useCallback(async () => {
    try {
      const res = await fetch('/api/vizier/pool');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (mountedRef.current && json.data) {
        const data = json.data as IndicatorPool;
        setPool(data);
        setLastUpdate(Date.now());
        setError(null);
        setLoading(false);

        const nextPrice = extractPriceData(data);
        if (priceDataChanged(prevPriceRef.current, nextPrice)) {
          prevPriceRef.current = nextPrice;
          setPriceData(nextPrice);
        }

        const nextMarket = extractMarketData(data);
        if (marketDataChanged(prevMarketRef.current, nextMarket)) {
          prevMarketRef.current = nextMarket;
          setMarketData(nextMarket);
        }
      }
    } catch (err: any) {
      if (mountedRef.current) {
        setError(err.message);
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchPool();
    const timer = setInterval(fetchPool, 20000);
    return () => {
      mountedRef.current = false;
      clearInterval(timer);
    };
  }, [fetchPool]);

  const poolValue = useMemo(() => ({ pool, loading, error, lastUpdate }), [pool, loading, error, lastUpdate]);
  const priceValue = useMemo(() => ({ priceData, loading }), [priceData, loading]);
  const marketValue = useMemo(() => ({ marketData, loading }), [marketData, loading]);

  return (
    <PoolContext.Provider value={poolValue}>
      <PriceContext.Provider value={priceValue}>
        <MarketContext.Provider value={marketValue}>{children}</MarketContext.Provider>
      </PriceContext.Provider>
    </PoolContext.Provider>
  );
}

export function usePool(): PoolContextType {
  return useContext(PoolContext);
}

export function usePricePool(): PriceContextType {
  return useContext(PriceContext);
}

export function useMarketPool(): MarketContextType {
  return useContext(MarketContext);
}
